import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { cnpjMask, validaCNPJ } from '@/utils';
import { createAdvertiser, updateAdvertiser } from '@/api/advertiserApi';
import { Advertiser } from '@/types/advertiser';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddModalProps {
    advertiser: Advertiser | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Advertiser) => void;
    onUpdateData: (newData: Advertiser) => void;
}

const AddEditAdvertiserModal = ({ advertiser, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const isEditing = !!advertiser;

    const validationSchema = Yup.object({
        name: Yup.string()
            .trim()
            .required('Nome é obrigatório'),
        cnpj: Yup.string()
            .nullable()
            .optional()
            .test('is-valid-cnpj', 'CNPJ inválido', (value: any) => value ? validaCNPJ(value) : true),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: advertiser?.name || '',
            cnpj: advertiser?.cnpj || '',
        },
        validationSchema,
        onSubmit: (values: any) => {
            setLoading(true);
            if (advertiser) {
                updateAdvertiser(values)
                    .then((res: any) => {
                        toggle();
                        onUpdateData(res);
                        toast.success('Anunciante atualizado com sucesso!');
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAdvertiser(values)
                    .then((res: any) => {
                        toggle();
                        onAddData(res);
                        toast.success('Anunciante cadastrado com sucesso!');
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggle}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Editar' : 'Adicionar'} anunciante</DialogTitle>
                    <DialogDescription>
                        {isEditing ? 'Edite as informações do anunciante.' : 'Preencha as informações para cadastrar um novo anunciante.'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                id="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.name && formik.errors.name ? 'border-destructive' : ''}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <p className="text-sm text-destructive">{formik.errors.name}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="cnpj">CNPJ</Label>
                            <Input
                                id="cnpj"
                                value={cnpjMask(formik.values.cnpj)}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.cnpj && formik.errors.cnpj ? 'border-destructive' : ''}
                            />
                            {formik.touched.cnpj && formik.errors.cnpj && (
                                <p className="text-sm text-destructive">{formik.errors.cnpj}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={toggle}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEditing ? 'Salvar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditAdvertiserModal;
